import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { ValidationHelpers } from '../../../../services/validation-helpers';

@autoinject
export class ApplePayValidator {
	constructor(private i18n: I18N) { }

	validatePaymentRequest(request: ApplePayJSV14.ApplePayPaymentRequest): string[] {
		let singleValidationErrors: string[] = [];
		let recurringValidationErrors: string[] = [];

		if (!request) {
			singleValidationErrors.push(this.i18n.i18next.t('invalidRequest', 'Invalid request'));
			return singleValidationErrors;
		}

		const supportsCredit: boolean = request.merchantCapabilities.some((capability) => capability === 'supportsCredit');
		const supportsDebit: boolean = request.merchantCapabilities.some((capability) => capability === 'supportsDebit');
		if (!supportsCredit && !supportsDebit) {
			singleValidationErrors.push(this.i18n.i18next.t('merchantMustAcceptDebitOrCreditCards', 'Merchant must accept Debit or Credit cards'));
		}

		if (!this.hasValidLineItemAmount(request.total)) {
			singleValidationErrors.push(this.i18n.i18next.t('amountMustBeGreaterThanZero', 'Amount must be greater than $0.00'));
		}

		if (!request.supportedNetworks || request.supportedNetworks.length === 0) {
			singleValidationErrors.push(this.i18n.i18next.t('invalidNetowrks', 'Invalid networks') + `: a valid network is required`);
		}
		else {
			// ApplePayToDo: find a better way to store supported networks
			const applePaySupportedNetworks: string[] = ['mastercard', 'discover', 'amex', 'cartesbancaires', 'chinaunionpay',
				'eftpos', 'electron', 'elo', 'girocard', 'inerac', 'jcb', 'mada', 'maestro', 'mir', 'privatelabel', 'visa', 'vpay'];
			let invalidNetworks = [];
			let supportedNetworksAreValid: boolean = true;
			request.supportedNetworks.forEach((network) => {
				// if a passed in network is not supported by Apple Pay, the request is invalid
				if (applePaySupportedNetworks.indexOf(network.toLowerCase()) === -1) {
					supportedNetworksAreValid = false;
					invalidNetworks.push(network);
				}
			});

			if (!supportedNetworksAreValid) {
				singleValidationErrors.push(this.i18n.i18next.t('invalidNetowrks', 'Invalid networks') + `: ${invalidNetworks.toString()}`);
			}
		}

		if (request.recurringPaymentRequest) {
			recurringValidationErrors.push(...this.validateRecurringPaymentRequest(request.recurringPaymentRequest));
		}

		return [...singleValidationErrors, ...recurringValidationErrors];
	}

	// recurring request gets built internally, to prevent confusion we display a general failure msg
	validateRecurringPaymentRequest(request: ApplePayJSV14.ApplePayRecurringPaymentRequest): string[] {
		let validationErrors: string[] = [];

		if (!request) {
			this.i18n.i18next.t('applePaySessionFailed', 'Failed to create Apple Pay Session, please contact customer support');
			return validationErrors;
		}

		if (!this.hasValidLineItemAmount(request.regularBilling)) {
			this.i18n.i18next.t('applePaySessionFailed', 'Failed to create Apple Pay Session, please contact customer support');
			return validationErrors;
		}

		if (!request.regularBilling.paymentTiming || request.regularBilling.paymentTiming !== 'recurring') {
			this.i18n.i18next.t('applePaySessionFailed', 'Failed to create Apple Pay Session, please contact customer support');
			return validationErrors;
		}

		if (!request.regularBilling.recurringPaymentStartDate) {
			this.i18n.i18next.t('applePaySessionFailed', 'Failed to create Apple Pay Session, please contact customer support');
			return validationErrors;
		}

		if (!request.regularBilling.recurringPaymentIntervalUnit) {
			this.i18n.i18next.t('applePaySessionFailed', 'Failed to create Apple Pay Session, please contact customer support');
			return validationErrors;
		}

		if (!request.regularBilling.recurringPaymentIntervalCount) {
			this.i18n.i18next.t('applePaySessionFailed', 'Failed to create Apple Pay Session, please contact customer support');
			return validationErrors;
		}

		if (!request.managementURL) {
			this.i18n.i18next.t('applePaySessionFailed', 'Failed to create Apple Pay Session, please contact customer support');
			return validationErrors;
		}

		return validationErrors;
	}

	private hasValidLineItemAmount(lineItem: ApplePayJSV14.ApplePayLineItem): boolean {
		const parsedAmount: number = parseInt(lineItem?.amount, 10);
		if (isNaN(parsedAmount)) {
			return false;
		}
		// pending line items can, have $0 amounts
		return lineItem.type === 'final' ? parsedAmount > 0 : true;
	}

	validateSignatureAndTerms(hasSignature: boolean, acceptedTerms: boolean): string[] {
		let validationErrors: string[] = [];

		if (!hasSignature) {
			validationErrors.push(this.i18n.i18next.t('signUsingNameOnAccount', 'Please sign using the account holder name'));
		}

		if (!acceptedTerms) {
			validationErrors.push(this.i18n.i18next.t('youMustAgreeToTermsToContinue', 'You must agree to the terms in order to continue.'));
		}

		return validationErrors;
	}

	validateBillingContact(billingContact: ApplePayJS.ApplePayPaymentContact): ApplePayJS.ApplePayError[] {
		let applePayErrors: ApplePayJS.ApplePayError[] = [];
		const generalError: ApplePayJS.ApplePayError = new ApplePayError('billingContactInvalid');
		const nameError: ApplePayJS.ApplePayError = new ApplePayError('billingContactInvalid', 'name', 'A valid name is required');

		if (!billingContact) {
			applePayErrors.push(generalError);
			return applePayErrors;
		}

		if (!billingContact.givenName || !billingContact.familyName) {
			applePayErrors.push(nameError);
		}

		applePayErrors.concat(this.validatePostalCode(billingContact.postalCode));

		return applePayErrors;
	}

	validatePostalCode(postalCode: string): ApplePayJS.ApplePayError[] {
		return (!postalCode || !ValidationHelpers.isZipCode(postalCode))
			? [new ApplePayError('billingContactInvalid', 'postalCode', 'A valid zip code is required')]
			: [];
	}

	validatePaymentMethodType(type: string): ApplePayJS.ApplePayError[] {
		const invalidPaymentTypeError: ApplePayJS.ApplePayError = new ApplePayError('unknown', undefined, `Invalid payment type: ${type}`);
		if (!type) {
			return [invalidPaymentTypeError];
		}

		switch (type?.toLowerCase() ?? '') {
			case 'credit':
			case 'debit':
			case 'prepaid':
				return [];
			default:
				return [invalidPaymentTypeError];
		}
	}
}
